import React from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Camera,
  Clock,
  Home,
  Factory,
  Landmark,
  Sparkles,
} from "lucide-react";

/* =====================================================================
   LiveInstallationGallery
   ---------------------------------------------------------------------
   Premium placeholder gallery — no fake photography. Every card is a
   dark-gradient blueprint tile with a construction-themed outline icon
   and a centered "COMING SOON" badge. When the user is ready to
   publish real content later, the placeholder can be swapped for a
   photograph without redesigning the section.
   ===================================================================== */

const CARDS = [
  {
    key: "commercial",
    category: "Commercial Tower Project",
    Icon: Building2,
    accent: "Real Installation Photos Coming Soon",
  },
  {
    key: "residential",
    category: "Residential High-Rise Project",
    Icon: Home,
    accent: "Project Documentation In Progress",
  },
  {
    key: "industrial",
    category: "Industrial Construction Project",
    Icon: Factory,
    accent: "Real Installation Photos Coming Soon",
  },
  {
    key: "infrastructure",
    category: "Infrastructure Project",
    Icon: Landmark,
    accent: "Project Documentation In Progress",
  },
];

/* Reusable inline SVG blueprint grid + faint construction lines behind
   the icon on every placeholder tile. Rendered once per card via
   <BlueprintBackdrop />. */
function BlueprintBackdrop() {
  return (
    <svg
      aria-hidden
      className="absolute inset-0 w-full h-full"
      viewBox="0 0 400 300"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <pattern
          id="lig-blueprint-grid"
          width="24"
          height="24"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M 24 0 L 0 0 0 24"
            fill="none"
            stroke="rgba(255,107,26,0.09)"
            strokeWidth="1"
          />
        </pattern>
        <pattern
          id="lig-blueprint-major"
          width="120"
          height="120"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M 120 0 L 0 0 0 120"
            fill="none"
            stroke="rgba(255,107,26,0.16)"
            strokeWidth="1"
          />
        </pattern>
      </defs>
      <rect width="400" height="300" fill="url(#lig-blueprint-grid)" />
      <rect width="400" height="300" fill="url(#lig-blueprint-major)" />
      {/* Structural silhouette lines */}
      <g
        stroke="rgba(255,107,26,0.18)"
        strokeWidth="1"
        strokeDasharray="4 4"
        fill="none"
      >
        <line x1="0" y1="230" x2="400" y2="230" />
        <line x1="80" y1="230" x2="80" y2="70" />
        <line x1="320" y1="230" x2="320" y2="70" />
      </g>
      <g stroke="rgba(255,255,255,0.05)" strokeWidth="1" fill="none">
        <line x1="0" y1="60" x2="400" y2="60" />
        <line x1="0" y1="150" x2="400" y2="150" />
      </g>
    </svg>
  );
}

export default function LiveInstallationGallery() {
  return (
    <section
      data-testid="live-installation-gallery"
      className="relative bg-liftile-black py-24 md:py-32 overflow-hidden"
    >
      {/* Subtle DNA — matches the site */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px bg-liftile-orange/25"
      />
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none opacity-[0.12]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(60px)",
        }}
      />

      <div className="container-x relative">
        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65 }}
          className="max-w-3xl mb-14 md:mb-16"
        >
          <div className="heading-eyebrow mb-3">Live Installation Gallery</div>
          <h2 className="display-2 text-liftile-cream">
            Real LIFTILE Machines{" "}
            <span className="text-liftile-orange">
              Working Across Construction Sites
            </span>
          </h2>
          <p className="font-body text-base md:text-lg text-liftile-cream/70 mt-6 leading-relaxed max-w-2xl">
            We are currently documenting real customer installations across
            India. High-quality photographs, working videos, completed project
            case studies and customer testimonials will be published here
            shortly. This section reflects our commitment to transparency,
            engineering excellence and real-world performance.
          </p>
        </motion.div>

        {/* ── Placeholder card grid ── */}
        <div
          data-testid="live-gallery-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-7"
        >
          {CARDS.map((c, i) => (
            <motion.article
              key={c.key}
              data-testid={`live-gallery-card-${c.key}`}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{
                duration: 0.6,
                delay: i * 0.08,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="group relative h-full flex flex-col rounded-2xl border border-white/[0.08] overflow-hidden hover:border-liftile-orange/50 hover:-translate-y-1 transition-all duration-500"
              style={{
                background:
                  "linear-gradient(155deg, rgba(20,20,20,0.85) 0%, rgba(15,15,15,0.95) 60%, rgba(10,10,10,1) 100%)",
              }}
            >
              {/* Blueprint tile — icon in center, "COMING SOON" pill above */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <BlueprintBackdrop />
                {/* soft radial glow behind icon */}
                <span
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 55%, rgba(255,107,26,0.18) 0%, rgba(255,107,26,0) 55%)",
                  }}
                />

                {/* COMING SOON pill — centered above icon */}
                <div className="absolute inset-x-0 top-6 flex justify-center">
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-liftile-orange/95 backdrop-blur-md px-3 py-1.5 shadow-[0_10px_30px_-8px_rgba(255,107,26,0.75)]">
                    <Clock className="w-3 h-3 text-white" strokeWidth={2.5} />
                    <span className="font-heading text-[10px] uppercase tracking-[0.24em] text-white font-semibold">
                      Coming Soon
                    </span>
                  </div>
                </div>

                {/* Central construction icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="relative flex items-center justify-center w-24 h-24 md:w-28 md:h-28 rounded-2xl border border-liftile-orange/25 transition-transform duration-500 group-hover:scale-105"
                    style={{
                      backgroundColor: "rgba(255,107,26,0.06)",
                      boxShadow:
                        "inset 0 0 40px rgba(255,107,26,0.08), 0 8px 30px -12px rgba(0,0,0,0.6)",
                    }}
                  >
                    <c.Icon
                      className="w-11 h-11 md:w-12 md:h-12 text-liftile-orange"
                      strokeWidth={1.4}
                    />
                    {/* Corner ticks — blueprint feel */}
                    {[
                      "top-1.5 left-1.5",
                      "top-1.5 right-1.5",
                      "bottom-1.5 left-1.5",
                      "bottom-1.5 right-1.5",
                    ].map((pos) => (
                      <span
                        key={pos}
                        className={`absolute ${pos} w-2 h-2 border-liftile-orange/60`}
                        style={{
                          borderTop: pos.includes("top") ? "1px solid" : "none",
                          borderBottom: pos.includes("bottom")
                            ? "1px solid"
                            : "none",
                          borderLeft: pos.includes("left") ? "1px solid" : "none",
                          borderRight: pos.includes("right")
                            ? "1px solid"
                            : "none",
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Orange top-right hover accent — matches Live Gallery DNA */}
                <span className="absolute top-0 right-0 h-[2px] w-0 bg-liftile-orange transition-all duration-500 group-hover:w-20 rounded-bl-full" />
              </div>

              {/* Card body */}
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-heading text-sm md:text-[15px] uppercase tracking-wider font-semibold text-liftile-cream leading-snug">
                  {c.category}
                </h3>

                {/* Accent status line with camera icon */}
                <div className="flex items-center gap-2 mt-3">
                  <Camera
                    className="w-3.5 h-3.5 text-liftile-orange shrink-0"
                    strokeWidth={2}
                  />
                  <span className="font-heading text-[10.5px] md:text-[11px] uppercase tracking-[0.18em] font-semibold text-liftile-orange">
                    {c.accent}
                  </span>
                </div>

                <p className="font-body text-[13px] text-liftile-cream/60 mt-3 leading-relaxed flex-1">
                  Real customer installation photographs and videos will be
                  available soon.
                </p>
              </div>
            </motion.article>
          ))}
        </div>

        {/* ── Centered CTA (disabled / non-navigable) ── */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="flex justify-center mt-14 md:mt-16"
        >
          <button
            type="button"
            disabled
            aria-disabled="true"
            data-testid="live-gallery-cta"
            className="group inline-flex items-center gap-3 rounded-full border border-white/25 bg-white/[0.03] text-liftile-cream font-heading font-semibold uppercase tracking-[0.16em] text-xs md:text-sm px-8 py-4 transition-all duration-400 hover:border-liftile-orange/60 hover:bg-white/[0.05] hover:-translate-y-0.5 cursor-not-allowed"
          >
            View Live Installations
            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] font-semibold text-liftile-orange">
              <Clock className="w-3 h-3" strokeWidth={2.5} />
              Coming Soon
            </span>
            <Sparkles
              className="w-4 h-4 text-liftile-orange"
              strokeWidth={2}
            />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
